/**
 * Gestor de WhatsApp, presentación «Bandeja» (2026-10-06).
 *
 * Reemplaza la lista estilo WhatsApp por una vista de trabajo que usa todo el
 * ancho: riel con el modo y bandejas inteligentes (Por responder, VIP,
 * Fijados, Grupos, etiquetas), lista agrupada por fecha, la conversación como
 * hilo de lectura (ConversacionGestor) y un panel con los datos del contacto.
 *
 * Conserva el comportamiento de la versión recuperada (2026-10-05): refresco
 * cada 5 s, bloqueo por inactividad, Escape para cerrar y presencia mientras
 * se usa. Qué chats esconde el modo lo decide el backend (ocultoPorModo).
 */
import "./gestor.css";
import "./temas.css";
import "./bandeja.css";
import {
  rec_EstadoError as EstadoError,
  rec_Lo as claseEntrada,
  rec_React as React,
  rec_fQ as INACTIVIDAD_MS,
  rec_ii as Girando,
  rec_pQ as PRESENCIA_MS,
  rec_vx as clienteGestor,
  rec_yQ as nombreTipo,
  rec_yx as esBloqueo,
} from "../../runtime/index.jsx";
import { clienteModos, describirActivo, opcionesProgramar, fechaHora } from "./api-modos.js";
import { ConversacionGestor } from "./ConversacionGestor.jsx";
import {
  IconoAjustes, IconoBandeja, IconoBuscar, IconoCandadoCerrar, IconoEstrella, IconoEtiqueta,
  IconoGrupo, IconoMas, IconoMenu, IconoOjo, IconoPin, IconoReloj,
} from "./iconos.jsx";
import { ModosGestor } from "./ModosGestor.jsx";
import { Avatar } from "./Avatar.jsx";
import { ListaHallazgos, ModalPinOcultos, ModalResumen, exportarChat } from "./herramientas.jsx";
import { TEMAS } from "./temas.js";
import { atraso, colorDe, finDeNumero, haceCuanto, iniciales, nombresRepetidos, seccionDe } from "./util-gestor.js";

// runtime/index.jsx importa este archivo antes de definir React (import
// circular): los hooks se leen al usarlos, no al cargar el módulo.
const useCallback = (...a) => React.useCallback(...a);
const useEffect = (...a) => React.useEffect(...a);
const useMemo = (...a) => React.useMemo(...a);
const useRef = (...a) => React.useRef(...a);
const useState = (...a) => React.useState(...a);
const nada = () => {};

/** Cierra un menú flotante al tocar fuera o con Escape. */
function usarMenu() {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    const tecla = (e) => { if (e.key === `Escape`) { e.stopPropagation(); setAbierto(false); } };
    document.addEventListener(`pointerdown`, fuera);
    document.addEventListener(`keydown`, tecla, true);
    return () => { document.removeEventListener(`pointerdown`, fuera); document.removeEventListener(`keydown`, tecla, true); };
  }, [abierto]);
  return { abierto, setAbierto, ref };
}

// ------------------------------------------------------------------ bandejas

const BANDEJAS = [
  { id: `todo`, nombre: `Todo`, Icono: IconoBandeja },
  { id: `responder`, nombre: `Por responder`, Icono: IconoReloj },
  { id: `vip`, nombre: `VIP`, Icono: IconoEstrella },
  { id: `fijados`, nombre: `Fijados`, Icono: IconoPin },
  { id: `grupos`, nombre: `Grupos`, Icono: IconoGrupo },
  { id: `programados`, nombre: `Programados`, Icono: IconoReloj },
  { id: `recordatorios`, nombre: `Recordatorios`, Icono: IconoReloj },
];

/**
 * Qué chats entran en una bandeja. Sin modo activo, «Todo» deja los grupos en
 * su propia bandeja (como el «Mostrar grupos» de antes); con modo, manda el modo.
 */
function enBandeja(c, bandeja, hayModo) {
  // Los ocultos (segundo PIN) sólo viven en su bandeja.
  if (c.oculto) return bandeja === `privados`;
  if (bandeja === `privados`) return false;
  if (bandeja === `ocultos`) return c.ocultoPorModo;
  if (c.ocultoPorModo) return false;
  if (bandeja.startsWith(`etiqueta:`)) return c.etiquetas?.includes(bandeja.slice(9));
  switch (bandeja) {
    case `responder`: return esperaReciente(c);
    case `vip`: return c.vip;
    case `fijados`: return c.fijado;
    case `grupos`: return c.esGrupo;
    case `programados`: return c.programados > 0;
    case `recordatorios`: return Boolean(c.recordatorio);
    default: return hayModo || !c.esGrupo || c.fijado || c.vip;
  }
}

/** «Por responder» mira 30 días: un mensaje de hace meses ya no es un pendiente. */
const VENTANA_RESPONDER_MS = 30 * 24 * 3_600_000;
const esperaReciente = (c) => c.esperaRespuesta && !c.esGrupo && !c.silenciado && Date.now() - new Date(c.ultimoMensajeEn) < VENTANA_RESPONDER_MS;

const DURACIONES = [[null, `Sin fin`], [30, `30 min`], [60, `1 h`], [120, `2 h`], [240, `4 h`]];

/** La tarjeta del modo en el riel: tocarla abre el cambio de modo. */
function ModoActual({ datos, onActivar, onConfigurar }) {
  const menu = usarMenu();
  const activo = datos?.activo;
  const modo = datos?.modos.find((m) => m.id === activo?.id) ?? null;
  const sinModoAMano = activo?.origen === `manual` && activo.id == null;
  return (
    <div className="gx-ancla" ref={menu.ref} style={{ display: `block` }}>
      <button type="button" className={`gx-modo-actual gx-c-${modo?.color ?? `gris`}`} aria-haspopup="menu"
        aria-expanded={menu.abierto} onClick={() => menu.setAbierto(!menu.abierto)}>
        <small>Modo</small>
        <b>{!datos ? `…` : modo ? modo.nombre : `Sin modo`}</b>
        <span>{modo ? describirActivo(activo) : sinModoAMano ? `a mano: se ve todo` : `se ve todo`}</span>
      </button>
      {menu.abierto && (
        <div className="gx-menu gx-menu-modos" role="menu">
          {datos?.modos.map((m) => (
            <div key={m.id} className={`gx-menu-modo gx-c-${m.color}`}>
              <span className="gx-menu-modo-nombre">{m.nombre}{activo?.id === m.id ? ` · activo` : ``}</span>
              <span className="gx-duraciones">
                {DURACIONES.map(([minutos, texto]) => (
                  <button key={texto} type="button" role="menuitem" aria-label={`${m.nombre} ${texto}`}
                    onClick={() => { menu.setAbierto(false); onActivar({ modoId: m.id, minutos }); }}>{texto}</button>
                ))}
              </span>
            </div>
          ))}
          <hr />
          <button type="button" role="menuitem" onClick={() => { menu.setAbierto(false); onActivar({ modoId: null, minutos: null }); }}>Sin modo (ver todo)</button>
          {activo?.origen === `manual` && (
            <button type="button" role="menuitem" onClick={() => { menu.setAbierto(false); onActivar({ automatico: true }); }}>Volver al horario</button>
          )}
          <button type="button" role="menuitem" onClick={() => { menu.setAbierto(false); onConfigurar(); }}>Configurar modos…</button>
        </div>
      )}
    </div>
  );
}

function SelectorTema({ tema, temaDelModo, onElegir }) {
  const menu = usarMenu();
  const actual = TEMAS.find((t) => t.id === (temaDelModo ?? tema)) ?? TEMAS[0];
  return (
    <span className="gx-ancla" ref={menu.ref} style={{ display: `block` }}>
      <button type="button" className="gx-boton chico" aria-haspopup="menu" onClick={() => menu.setAbierto(!menu.abierto)}>
        <span className="gx-muestra" aria-hidden="true">{actual.muestra.map((c) => <i key={c} style={{ background: c }} />)}</span>
        Tema · {actual.nombre}
      </button>
      {menu.abierto && (
        <div className="gx-menu gx-temas" role="menu" style={{ top: `auto`, bottom: `calc(100% + 6px)` }}>
          <span className="gx-titulo-menu">Tema</span>
          {TEMAS.map((t) => (
            <button key={t.id} type="button" role="menuitemradio" aria-checked={tema === t.id}
              onClick={() => { menu.setAbierto(false); onElegir(t.id); }}>
              <span className="gx-tema-opcion">
                <span className="gx-muestra" aria-hidden="true">{t.muestra.map((c) => <i key={c} style={{ background: c }} />)}</span>
                <span>{t.nombre}<small>{t.ayuda}</small></span>
              </span>
            </button>
          ))}
          {temaDelModo && <span className="gx-titulo-menu" style={{ textTransform: `none`, letterSpacing: 0 }}>El modo activo usa su propio tema mientras dure.</span>}
        </div>
      )}
    </span>
  );
}

/** Editor de etiquetas de un chat (en el menú ··· y en el panel de contacto). */
function EtiquetasChat({ chat, conocidas, onCambiar }) {
  const [nueva, setNueva] = useState(``);
  const etiquetas = chat.etiquetas ?? [];
  const todas = [...new Set([...conocidas, ...etiquetas])].sort((a, b) => a.localeCompare(b, `es`));
  const alternar = (e) => onCambiar({ etiquetas: etiquetas.includes(e) ? etiquetas.filter((x) => x !== e) : [...etiquetas, e] });
  const agregar = (ev) => {
    ev.preventDefault();
    const t = nueva.trim();
    setNueva(``);
    if (t && !etiquetas.some((x) => x.toLowerCase() === t.toLowerCase())) onCambiar({ etiquetas: [...etiquetas, t] });
  };
  return (
    <div className="gx-opciones">
      {todas.map((e) => (
        <label key={e} className="gx-opcion">
          <input type="checkbox" checked={etiquetas.includes(e)} onChange={() => alternar(e)} /> {e}
        </label>
      ))}
      <form onSubmit={agregar} className="gx-fila">
        <input className="gx-entrada" style={{ flex: 1, padding: `6px 8px`, fontSize: 13 }} maxLength={30}
          placeholder="Nueva etiqueta" aria-label="Nueva etiqueta" value={nueva} onChange={(e) => setNueva(e.target.value)} />
        <button type="submit" className="gx-boton chico">Añadir</button>
      </form>
    </div>
  );
}

function AjustesChat({ chat, conocidas, onCambiar, onRenombrar, onOcultar }) {
  const menu = usarMenu();
  return (
    <span className="gx-ancla" ref={menu.ref} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button type="button" className="gx-icono" aria-label={`Ajustes de ${chat.nombre}`} aria-haspopup="menu"
        aria-expanded={menu.abierto} onClick={() => menu.setAbierto(!menu.abierto)}><IconoMas /></button>
      {menu.abierto && (
        <div className="gx-menu derecha" role="menu">
          <button type="button" role="menuitemcheckbox" aria-checked={chat.vip} onClick={() => onCambiar({ vip: !chat.vip })}>
            {chat.vip ? `Quitar de VIP` : `Marcar VIP · se ve en cualquier modo`}
          </button>
          <button type="button" role="menuitemcheckbox" aria-checked={chat.fijado} onClick={() => onCambiar({ fijado: !chat.fijado })}>
            {chat.fijado ? `Desfijar` : `Fijar arriba`}
          </button>
          {onRenombrar && <button type="button" role="menuitem" onClick={() => { menu.setAbierto(false); onRenombrar(); }}>Cambiar nombre…</button>}
          <button type="button" role="menuitemcheckbox" aria-checked={chat.silenciado} onClick={() => onCambiar({ silenciado: !chat.silenciado })}>
            {chat.silenciado ? `Quitar silencio` : `Silenciar · no cuenta en «Por responder»`}
          </button>
          {onOcultar && (
            <button type="button" role="menuitem" onClick={() => { menu.setAbierto(false); onOcultar(!chat.oculto); }}>
              {chat.oculto ? `Dejar de ocultar` : `Ocultar con PIN…`}
            </button>
          )}
          <hr />
          <span className="gx-titulo-menu">Etiquetas</span>
          <div style={{ padding: `2px 10px 6px` }}><EtiquetasChat chat={chat} conocidas={conocidas} onCambiar={onCambiar} /></div>
        </div>
      )}
    </span>
  );
}

function Tarjeta({ chat, api, elegida, repetido, conocidas, onElegir, onCambiar, onRenombrar, onOcultar }) {
  const espera = esperaReciente(chat);
  const tarde = espera ? atraso(chat.ultimoMensajeEn) : ``;
  return (
    <div className={`gx-tarjeta${elegida ? ` elegida` : ``}`} role="button" tabIndex={0} aria-current={elegida}
      onClick={onElegir} onKeyDown={(e) => { if (e.key === `Enter` || e.key === ` `) { e.preventDefault(); onElegir(); } }}>
      <Avatar chat={chat} api={api} />
      <span className="gx-tarjeta-nombre">
        {chat.vip && <span className="gx-vip" title="VIP"><IconoEstrella /></span>}
        <span>{chat.nombre}</span>
        {repetido && chat.numero && <em>{finDeNumero(chat.numero)}</em>}
        {chat.fijado && <span className="gx-fijado" title="Fijado"><IconoPin /></span>}
        {chat.silenciado && <span className="gx-silencio" title="Silenciado">🔕</span>}
      </span>
      <span className="gx-tarjeta-hora">{espera ? `` : haceCuanto(chat.ultimoMensajeEn)}</span>
      <span className="gx-tarjeta-previa" title={chat.etiquetas?.length ? `Etiquetas: ${chat.etiquetas.join(`, `)}` : undefined}>
        {chat.vistaPrevia ?? nombreTipo(chat.tipoUltimo)}
      </span>
      <span className="gx-tarjeta-pie">
        {chat.recordatorio && (
          <span className="gx-recordar-tarjeta" title="Recordatorio">⏰ {new Date(chat.recordatorio) < new Date() ? `ahora` : fechaHora(chat.recordatorio)}</span>
        )}
        {chat.programados > 0 && <span className="gx-prog" title="Mensajes programados">⏱ {chat.programados}</span>}
        {espera && (
          <span className={`gx-espera ${tarde}`} title="Espera tu respuesta">espera {haceCuanto(chat.ultimoMensajeEn)}</span>
        )}
      </span>
      <AjustesChat chat={chat} conocidas={conocidas} onCambiar={onCambiar} onRenombrar={onRenombrar} onOcultar={onOcultar} />
    </div>
  );
}

/** Cambiar el nombre de un chat en el gestor (no toca WhatsApp). Vacío = el de WhatsApp. */
function EditorNombre({ chat, onCambiar, onListo, autoFocus = false }) {
  const [nombre, setNombre] = useState(chat.nombre);
  useEffect(() => { setNombre(chat.nombre); }, [chat.id, chat.nombre]);
  const guardar = (e) => {
    e.preventDefault();
    const t = nombre.trim();
    onCambiar({ nombre: t === (chat.nombreWhatsApp ?? ``) ? `` : t });
    onListo?.();
  };
  return (
    <form className="gx-nombre-editar" onSubmit={guardar}>
      <input className="gx-entrada" aria-label="Nombre en el gestor" maxLength={60} autoFocus={autoFocus} value={nombre}
        placeholder={chat.nombreWhatsApp ?? chat.numero ?? `Nombre`} onChange={(e) => setNombre(e.target.value)}
        onKeyDown={(e) => { if (e.key === `Escape`) { e.stopPropagation(); onListo?.(); } }} />
      <button type="submit" className="gx-boton chico" disabled={nombre.trim() === chat.nombre}>Guardar</button>
    </form>
  );
}

function PanelContacto({ chat, api, cliente, conocidas, onCambiar, onCerrar, editandoNombre, onEditarNombre, onOcultar }) {
  const [exportando, setExportando] = useState(``);
  const exportar = async () => {
    setExportando(`Preparando…`);
    try {
      const n = await exportarChat({ chat, api, cliente, alAvanzar: (k) => setExportando(`Trayendo mensajes… ${k}`) });
      setExportando(`Listo: ${n} mensajes. En la ventana nueva elige «Guardar como PDF».`);
    } catch (e) { setExportando(e.message); }
  };
  return (
    <aside className="gx-info" aria-label="Datos del contacto">
      <section className="gx-info-perfil">
        <Avatar chat={chat} api={api} grande />
        {editandoNombre
          ? <EditorNombre chat={chat} onCambiar={onCambiar} onListo={() => onEditarNombre(false)} autoFocus />
          : <button type="button" className="gx-enlace" style={{ textDecoration: `none`, fontSize: 16, fontWeight: 600, color: `var(--text-h)` }}
              title="Cambiar nombre" onClick={() => onEditarNombre(true)}>{chat.nombre} ✎</button>}
        <span className="gx-texto">{chat.esGrupo ? `Grupo` : chat.numero ?? `Sin número conocido`}</span>
        {chat.nombreWhatsApp && (
          <span className="gx-texto">En WhatsApp: {chat.nombreWhatsApp} · <button type="button" className="gx-enlace" onClick={() => onCambiar({ nombre: `` })}>usar ese</button></span>
        )}
      </section>
      <section>
        <h3>En el gestor</h3>
        <label className="gx-interruptor">VIP · se ve en cualquier modo
          <input type="checkbox" checked={chat.vip} onChange={() => onCambiar({ vip: !chat.vip })} />
        </label>
        <label className="gx-interruptor">Fijado arriba
          <input type="checkbox" checked={chat.fijado} onChange={() => onCambiar({ fijado: !chat.fijado })} />
        </label>
        <label className="gx-interruptor">Silenciado · no cuenta en «Por responder»
          <input type="checkbox" checked={chat.silenciado} onChange={() => onCambiar({ silenciado: !chat.silenciado })} />
        </label>
        <label className="gx-interruptor">Oculto · sólo con el segundo PIN
          <input type="checkbox" checked={chat.oculto} onChange={() => onOcultar(!chat.oculto)} />
        </label>
      </section>
      <section>
        <h3>Etiquetas</h3>
        <EtiquetasChat chat={chat} conocidas={conocidas} onCambiar={onCambiar} />
      </section>
      <section>
        <h3>Actividad</h3>
        <div className="gx-dato"><span>Último mensaje</span><span>hace {haceCuanto(chat.ultimoMensajeEn)}</span></div>
        <div className="gx-dato"><span>Estado</span><span>{chat.esperaRespuesta ? `Espera tu respuesta` : `Al día`}</span></div>
        {chat.ocultoPorModo && <div className="gx-dato"><span>Modo actual</span><span>Lo esconde</span></div>}
      </section>
      <section>
        <h3>Exportar</h3>
        <button type="button" className="gx-boton chico" onClick={exportar} disabled={exportando.startsWith(`Trayendo`) || exportando === `Preparando…`}>Exportar chat a PDF</button>
        {exportando && <small className="gx-texto" role="status">{exportando}</small>}
      </section>
      <button type="button" className="gx-boton chico" onClick={onCerrar}>Cerrar panel</button>
    </aside>
  );
}

/** Escribir a un número que no está en la lista (ahora o programado). */
function NuevoChat({ api, onCreado, onCerrar }) {
  const [numero, setNumero] = useState(``);
  const [texto, setTexto] = useState(``);
  const [cuando, setCuando] = useState(``);
  const [otra, setOtra] = useState(``);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(``);
  const opciones = useMemo(() => opcionesProgramar(), []);
  const enviar = async (e) => {
    e.preventDefault();
    setEnviando(true); setError(``);
    const programadoPara = cuando === `` ? null : cuando === `otra` ? (otra ? new Date(otra) : null) : opciones[Number(cuando)].cuando;
    try {
      const { chatId } = await api.nuevoChat({ numero, texto: texto.trim(), programadoPara });
      onCreado(chatId);
    } catch (err) {
      setError(err.message || `No se pudo crear.`);
      setEnviando(false);
    }
  };
  return (
    <div className="gx-modal" onPointerDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}
      onKeyDown={(e) => { if (e.key === `Escape`) { e.stopPropagation(); onCerrar(); } }}>
      <form className="gx-modal-caja" role="dialog" aria-modal="true" aria-label="Nuevo chat" onSubmit={enviar}>
        <h2>Escribir a un número nuevo</h2>
        <label className="gx-campo">Número
          <input className="gx-entrada" autoFocus inputMode="tel" placeholder="+51 987 654 321" value={numero} onChange={(e) => setNumero(e.target.value)} />
          <small>Con 9 dígitos se asume Perú (+51). Si no tiene WhatsApp, te avisa en el chat.</small>
        </label>
        <label className="gx-campo">Mensaje
          <textarea className="gx-area" maxLength={4000} value={texto} onChange={(e) => setTexto(e.target.value)} />
        </label>
        <label className="gx-campo">Cuándo
          <select className="gx-select" value={cuando} onChange={(e) => setCuando(e.target.value)}>
            <option value="">Ahora</option>
            {opciones.map((o, i) => <option key={o.texto} value={i}>{o.texto} · {fechaHora(o.cuando)}</option>)}
            <option value="otra">Otra fecha y hora…</option>
          </select>
          {cuando === `otra` && <input type="datetime-local" className="gx-entrada" aria-label="Fecha y hora" value={otra} onChange={(e) => setOtra(e.target.value)} />}
        </label>
        {error && <p className="gx-error" role="alert">{error}</p>}
        <div className="gx-fila">
          <span className="gx-espacio" />
          <button type="button" className="gx-boton" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="gx-boton primario" disabled={enviando || !numero.trim() || !texto.trim() || (cuando === `otra` && !otra)}>
            {cuando === `` ? `Enviar` : `Programar`}
          </button>
        </div>
      </form>
    </div>
  );
}

// ------------------------------------------------------------------ principal

export function ConversacionesGestor({ llave, onCerrar }) {
  const cliente = useMemo(() => clienteGestor(llave), [llave]);
  const [ocultosLlave, setOcultosLlave] = useState(null);
  const modosApi = useMemo(() => clienteModos(llave, ocultosLlave), [llave, ocultosLlave]);
  const [chats, setChats] = useState(null);
  const [error, setError] = useState(``);
  const [modos, setModos] = useState(null);
  const [errorModos, setErrorModos] = useState(``);
  const [buscar, setBuscar] = useState(``);
  const [bandeja, setBandeja] = useState(`todo`);
  const [elegidoId, setElegidoId] = useState(null);
  const [info, setInfo] = useState(() => window.innerWidth >= 1280);
  const [rielAbierto, setRielAbierto] = useState(false);
  const [configurando, setConfigurando] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [recordatorios, setRecordatorios] = useState([]);
  const [resumenAbierto, setResumenAbierto] = useState(false);
  const ultimaActividad = useRef(0);
  const buscador = useRef(null);

  const cerrar = useCallback(() => {
    onCerrar();
    cliente.presencia(false).catch(nada);
    cliente.cerrar().catch(nada);
  }, [cliente, onCerrar]);

  const alFallar = useCallback((e) => {
    if (esBloqueo(e)) { onCerrar(); return; }
    setError(e.message);
  }, [onCerrar]);

  const cargarChats = useCallback(async () => {
    try { setChats(await modosApi.chats()); setError(``); } catch (e) { alFallar(e); }
  }, [modosApi, alFallar]);

  const cargarModos = useCallback(async () => {
    try { setModos(await modosApi.modos()); setErrorModos(``); } catch (e) {
      if (esBloqueo(e)) { onCerrar(); return; }
      setErrorModos(e.message);
    }
  }, [modosApi, onCerrar]);

  useEffect(() => { cargarChats(); const t = setInterval(cargarChats, 5e3); return () => clearInterval(t); }, [cargarChats]);
  useEffect(() => { cargarModos(); const t = setInterval(cargarModos, 30e3); return () => clearInterval(t); }, [cargarModos]);
  const cargarPlantillas = useCallback(() => modosApi.plantillas().then(setPlantillas).catch(nada), [modosApi]);
  useEffect(() => { cargarPlantillas(); }, [cargarPlantillas]);
  const cargarRecordatorios = useCallback(() => modosApi.recordatorios().then(setRecordatorios).catch(nada), [modosApi]);
  useEffect(() => { cargarRecordatorios(); const t = setInterval(cargarRecordatorios, 30e3); return () => clearInterval(t); }, [cargarRecordatorios]);
  const vencidos = recordatorios.filter((r) => new Date(r.para) <= new Date()).length;

  // Eliminados nuevos y «ver una vez» sin abrir (para el riel).
  const [contadores, setContadores] = useState({ eliminadosNuevos: 0, unaVezPendientes: 0 });
  const cargarContadores = useCallback(() => modosApi.contadores().then(setContadores).catch(nada), [modosApi]);
  useEffect(() => { cargarContadores(); const t = setInterval(cargarContadores, 30e3); return () => clearInterval(t); }, [cargarContadores]);

  // Chats ocultos: segundo PIN. pinModal = null | { configurado, ocultarChatId? }
  const [pinModal, setPinModal] = useState(null);
  const pedirOcultos = async (ocultarChatId = null) => {
    try { const { pinConfigurado } = await modosApi.ocultos(); setPinModal({ configurado: pinConfigurado, ocultarChatId }); } catch (e) { setError(e.message); }
  };
  const alAbrirOcultos = async (llaveOcultos) => {
    const pendiente = pinModal?.ocultarChatId;
    setPinModal(null);
    setOcultosLlave(llaveOcultos);
    if (pendiente) await clienteModos(llave, llaveOcultos).ajustarChat(pendiente, { oculto: true });
    setBandeja(`privados`);
  };
  const cerrarOcultos = async () => {
    try { await modosApi.cerrarOcultos(); } catch { /* igual se olvida */ }
    setOcultosLlave(null);
    if (bandeja === `privados`) setBandeja(`todo`);
  };
  const ocultarChat = async (chatId, ocultar) => {
    if (ocultar && !ocultosLlave) { await pedirOcultos(chatId); return; }
    await cambiarChat(chatId, { oculto: ocultar });
    if (ocultar && elegidoId === chatId) setElegidoId(null);
  };

  // Listas de mensajes: eliminados y «ver una vez».
  const [hallazgos, setHallazgos] = useState(null);
  const cargarHallazgos = useCallback(async (b) => {
    setHallazgos(null);
    try {
      if (b === `eliminados`) { setHallazgos((await modosApi.eliminados()).lista); modosApi.eliminadosVistos().then(cargarContadores).catch(nada); }
      if (b === `unavez`) setHallazgos(await modosApi.unaVez());
    } catch (e) { setError(e.message); setHallazgos([]); }
  }, [modosApi, cargarContadores]);
  const anotarHallazgo = async (h, cambios) => {
    try { await modosApi.anotar(h.mensajeId, cambios); await cargarHallazgos(`unavez`); cargarContadores(); } catch (e) { setError(e.message); }
  };

  // Búsqueda de mensajes (además de los chats) desde 2 letras.
  const [mensajesEncontrados, setMensajesEncontrados] = useState(undefined);
  useEffect(() => {
    const q = buscar.trim();
    if (q.length < 2) { setMensajesEncontrados(undefined); return undefined; }
    const t = setTimeout(() => {
      setMensajesEncontrados(null);
      modosApi.buscar({ q }).then(setMensajesEncontrados).catch(() => setMensajesEncontrados([]));
    }, 400);
    return () => clearTimeout(t);
  }, [buscar, modosApi]);

  // Aviso cuando el modo cambia solo (por horario), no cuando lo cambia el dueño.
  const modoPrevio = useRef(undefined);
  const cambioManual = useRef(false);
  const [avisoModo, setAvisoModo] = useState(``);
  useEffect(() => {
    if (!modos) return;
    const id = modos.activo?.id ?? null;
    if (modoPrevio.current !== undefined && modoPrevio.current !== id && !cambioManual.current) {
      const antes = modos.modos.find((m) => m.id === modoPrevio.current)?.nombre;
      const ahora = modos.modos.find((m) => m.id === id)?.nombre;
      setAvisoModo(ahora ? `Empezó el modo ${ahora}${modos.activo?.origen === `horario` ? ` (por horario)` : ``}` : `Terminó el modo ${antes ?? ``}`);
    }
    cambioManual.current = false;
    modoPrevio.current = id;
  }, [modos]);
  useEffect(() => { if (!avisoModo) return undefined; const t = setTimeout(() => setAvisoModo(``), 120e3); return () => clearTimeout(t); }, [avisoModo]);
  useEffect(() => { setEditandoNombre(false); }, [elegidoId]);

  // «En línea» mientras la página está visible y en uso (un modo invisible lo anula en el bot).
  useEffect(() => {
    const avisar = () => {
      const enUso = document.visibilityState === `visible` && Date.now() - ultimaActividad.current < 12e4;
      cliente.presencia(enUso).catch(nada);
    };
    avisar();
    const t = setInterval(avisar, PRESENCIA_MS);
    document.addEventListener(`visibilitychange`, avisar);
    return () => { clearInterval(t); document.removeEventListener(`visibilitychange`, avisar); cliente.presencia(false).catch(nada); };
  }, [cliente]);

  const hayModo = modos?.activo?.id != null;
  const repetidos = useMemo(() => nombresRepetidos(chats), [chats]);

  const enEsta = useMemo(() => {
    if (!chats) return [];
    const q = buscar.trim().toLowerCase();
    return chats.filter((c) => enBandeja(c, bandeja, hayModo)
      && (!q || `${c.nombre} ${c.vistaPrevia ?? ``} ${c.numero ?? ``}`.toLowerCase().includes(q)));
  }, [chats, bandeja, hayModo, buscar]);

  /** Secciones de la lista. En «Por responder», lo que más espera va primero. */
  const secciones = useMemo(() => {
    const porFecha = (a, b) => String(b.ultimoMensajeEn ?? ``).localeCompare(String(a.ultimoMensajeEn ?? ``));
    if (bandeja === `responder`) return [[`Esperando respuesta · lo más antiguo primero`, [...enEsta].sort((a, b) => -porFecha(a, b))]];
    if (bandeja === `recordatorios`) {
      const orden = [...enEsta].sort((a, b) => String(a.recordatorio).localeCompare(String(b.recordatorio)));
      const ahora = new Date();
      return [[`Vencidos`, orden.filter((c) => new Date(c.recordatorio) <= ahora)], [`Próximos`, orden.filter((c) => new Date(c.recordatorio) > ahora)]]
        .filter(([, cs]) => cs.length);
    }
    const mapa = new Map();
    const fijados = bandeja === `fijados` ? [] : enEsta.filter((c) => c.fijado).sort(porFecha);
    if (fijados.length) mapa.set(`Fijados`, fijados);
    for (const c of [...enEsta].sort(porFecha)) {
      if (fijados.includes(c)) continue;
      const s = seccionDe(c.ultimoMensajeEn);
      if (!mapa.has(s)) mapa.set(s, []);
      mapa.get(s).push(c);
    }
    return [...mapa];
  }, [enEsta, bandeja]);
  const orden = useMemo(() => secciones.flatMap(([, cs]) => cs), [secciones]);

  const cuentas = useMemo(() => {
    const r = {};
    for (const b of [...BANDEJAS.map((x) => x.id), `ocultos`, `privados`, ...(modos?.etiquetas ?? []).map((e) => `etiqueta:${e}`)]) {
      r[b] = (chats ?? []).filter((c) => enBandeja(c, b, hayModo)).length;
    }
    return r;
  }, [chats, hayModo, modos?.etiquetas]);

  // Bloqueo por inactividad; atajos: «/» busca, Alt+↑/↓ cambia de chat, Escape cierra.
  useEffect(() => {
    const marcar = () => { ultimaActividad.current = Date.now(); };
    marcar();
    const tecla = (e) => {
      marcar();
      const escribiendo = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName);
      if (e.key === `/` && !escribiendo) { e.preventDefault(); buscador.current?.focus(); return; }
      if (e.altKey && (e.key === `ArrowDown` || e.key === `ArrowUp`) && orden.length) {
        e.preventDefault();
        const i = orden.findIndex((c) => c.id === elegidoId);
        const j = e.key === `ArrowDown` ? Math.min(i + 1, orden.length - 1) : Math.max(i - 1, 0);
        setElegidoId(orden[i < 0 ? 0 : j].id);
        return;
      }
      if (e.key !== `Escape` || configurando || nuevoAbierto || resumenAbierto || pinModal) return;
      if (rielAbierto) { setRielAbierto(false); return; }
      if (escribiendo && e.target === buscador.current && buscar) { setBuscar(``); return; }
      cerrar();
    };
    window.addEventListener(`pointerdown`, marcar);
    window.addEventListener(`keydown`, tecla);
    const t = setInterval(() => { if (Date.now() - ultimaActividad.current > INACTIVIDAD_MS) cerrar(); }, 15e3);
    return () => { window.removeEventListener(`pointerdown`, marcar); window.removeEventListener(`keydown`, tecla); clearInterval(t); };
  }, [cerrar, configurando, nuevoAbierto, resumenAbierto, pinModal, orden, elegidoId, rielAbierto, buscar]);

  const activar = useCallback(async (pedido) => {
    cambioManual.current = true;
    setAvisoModo(``);
    try {
      const activo = await modosApi.activar(pedido);
      setModos((d) => (d ? { ...d, activo } : d));
      await cargarChats();
    } catch (e) { setErrorModos(e.message); }
  }, [modosApi, cargarChats]);

  const cambiarChat = useCallback(async (chatId, cambios) => {
    // Optimista: se ve al instante; si falla, el próximo refresco lo corrige.
    setChats((cs) => cs?.map((c) => (c.id === chatId ? { ...c, ...cambios } : c)));
    try {
      await modosApi.ajustarChat(chatId, cambios);
      await Promise.all([cargarChats(), cambios.etiquetas ? cargarModos() : null]);
    } catch (e) { setError(e.message); }
  }, [modosApi, cargarChats, cargarModos]);

  const elegirTema = useCallback(async (tema) => {
    setModos((d) => (d ? { ...d, tema } : d));
    try { await modosApi.guardarTema(tema); } catch (e) { setErrorModos(e.message); cargarModos(); }
  }, [modosApi, cargarModos]);

  const elegirBandeja = (b) => {
    setBandeja(b); setRielAbierto(false);
    if (b === `eliminados` || b === `unavez`) cargarHallazgos(b);
  };
  const esLista = bandeja === `eliminados` || bandeja === `unavez`;
  const abrirHallazgo = (h) => { setElegidoId(h.chatId); };

  const modoActivo = modos?.modos.find((m) => m.id === modos.activo?.id) ?? null;
  const tema = modoActivo?.tema ?? modos?.tema ?? `vanguard`;
  const elegido = chats?.find((c) => c.id === elegidoId) ?? null;
  const conocidas = modos?.etiquetas ?? [];
  const titulo = bandeja === `ocultos` ? `Ocultos por el modo` : bandeja === `privados` ? `Chats ocultos` : bandeja === `eliminados` ? `Eliminados`
    : bandeja === `unavez` ? `Ver una vez` : bandeja.startsWith(`etiqueta:`) ? bandeja.slice(9)
    : BANDEJAS.find((b) => b.id === bandeja)?.nombre;

  if (!chats) {
    return error ? <EstadoError mensaje={error} onReintentar={cargarChats} /> : (
      <div className="flex justify-center py-16 text-(--text)"><Girando className="h-6 w-6 animate-spin" /></div>
    );
  }

  return (
    <div className={`gx gx-bandeja${elegido ? ` con-chat` : ``}${elegido && info ? ` con-info` : ``}${rielAbierto ? ` riel-abierto` : ``}`} data-tema={tema}
      onClick={(e) => { if (rielAbierto && e.target === e.currentTarget) setRielAbierto(false); }}>
      <nav className="gx-riel" aria-label="Bandejas">
        <div>
          <div className="gx-riel-titulo">WhatsApp</div>
          <div className={`gx-presencia${modoActivo?.presencia === `invisible` ? ` invisible` : ``}`}>
            <i />{modoActivo?.presencia === `invisible` ? `Invisible: no apareces en línea` : `En línea mientras lo usas · el celular no suena`}
          </div>
        </div>
        <ModoActual datos={modos} onActivar={activar} onConfigurar={() => setConfigurando(true)} />
        {avisoModo && <div className="gx-aviso-modo" role="status">⏱ {avisoModo}</div>}
        {errorModos && <p className="gx-error">{errorModos}</p>}

        <div className="gx-grupo-nav">
          {BANDEJAS.map(({ id, nombre, Icono }) => (
            <button key={id} type="button" className={`gx-nav${bandeja === id ? ` activa` : ``}`} aria-current={bandeja === id} onClick={() => elegirBandeja(id)}>
              <Icono /><span>{nombre}</span>
              <span className={`gx-cuenta${(id === `responder` && cuentas.responder > 0) || (id === `recordatorios` && vencidos > 0) ? ` alerta` : ``}`}
                title={id === `recordatorios` && vencidos ? `${vencidos} vencidos` : undefined}>{cuentas[id]}</span>
            </button>
          ))}
          <button type="button" className={`gx-nav${bandeja === `eliminados` ? ` activa` : ``}`} onClick={() => elegirBandeja(`eliminados`)}>
            <i className="gx-nav-ico" aria-hidden="true">🗑</i><span>Eliminados</span>
            {contadores.eliminadosNuevos > 0 && <span className="gx-cuenta alerta" title="Nuevos desde la última vez">{contadores.eliminadosNuevos}</span>}
          </button>
          <button type="button" className={`gx-nav${bandeja === `unavez` ? ` activa` : ``}`} onClick={() => elegirBandeja(`unavez`)}>
            <i className="gx-nav-ico" aria-hidden="true">👁</i><span>Ver una vez</span>
            {contadores.unaVezPendientes > 0 && <span className="gx-cuenta alerta" title="Sin abrir en el celular">{contadores.unaVezPendientes}</span>}
          </button>
          <button type="button" className={`gx-nav${bandeja === `privados` ? ` activa` : ``}`}
            onClick={() => (ocultosLlave ? elegirBandeja(`privados`) : pedirOcultos())}>
            <i className="gx-nav-ico" aria-hidden="true">{ocultosLlave ? `🔓` : `🔒`}</i><span>Ocultos</span>
            {ocultosLlave && <span className="gx-cuenta">{cuentas.privados ?? 0}</span>}
          </button>
          {cuentas.ocultos > 0 && (
            <button type="button" className={`gx-nav${bandeja === `ocultos` ? ` activa` : ``}`} onClick={() => elegirBandeja(`ocultos`)}>
              <IconoOjo /><span>Ocultos por el modo</span><span className="gx-cuenta">{cuentas.ocultos}</span>
            </button>
          )}
        </div>

        {conocidas.length > 0 && (
          <div className="gx-grupo-nav">
            <h3>Etiquetas</h3>
            {conocidas.map((e) => (
              <button key={e} type="button" className={`gx-nav${bandeja === `etiqueta:${e}` ? ` activa` : ``}`} onClick={() => elegirBandeja(`etiqueta:${e}`)}>
                <IconoEtiqueta /><span>{e}</span><span className="gx-cuenta">{cuentas[`etiqueta:${e}`] ?? 0}</span>
              </button>
            ))}
          </div>
        )}

        <div className="gx-riel-pie">
          <button type="button" className="gx-boton chico" onClick={() => setResumenAbierto(true)}>✨ Resumen con IA</button>
          <button type="button" className="gx-boton chico" onClick={() => setConfigurando(true)}><IconoAjustes /> Configurar modos</button>
          {modos && <SelectorTema tema={modos.tema} temaDelModo={modoActivo?.tema ?? null} onElegir={elegirTema} />}
          <button type="button" className="gx-boton chico" onClick={cerrar}><IconoCandadoCerrar /> Ocultar (candado)</button>
        </div>
      </nav>

      <section className="gx-lista" aria-label={titulo}>
        <div className="gx-lista-cab">
          <h2>
            <button type="button" className="gx-icono gx-solo-movil" aria-label="Bandejas" onClick={() => setRielAbierto(true)}><IconoMenu /></button>
            {titulo} <small>{esLista ? (hallazgos?.length ?? ``) : enEsta.length}</small>
            {bandeja === `privados` && <button type="button" className="gx-boton chico" onClick={cerrarOcultos}>🔒 Cerrar</button>}
            <span className="gx-espacio" />
            <button type="button" className="gx-boton chico" onClick={() => setNuevoAbierto(true)} title="Escribir a un número nuevo">+ Nuevo</button>
          </h2>
          <label className="gx-buscador">
            <IconoBuscar />
            <input ref={buscador} aria-label="Buscar chat" className={claseEntrada} placeholder="Buscar nombre, número o texto"
              value={buscar} onChange={(e) => setBuscar(e.target.value)} />
            {!buscar && <kbd>/</kbd>}
          </label>
          {error && <p role="alert" className="gx-alerta">No se pudo actualizar. Lo de abajo es de hace un momento.</p>}
        </div>
        <div className="gx-lista-cuerpo">
          {esLista && (
            <ListaHallazgos lista={hallazgos} modo={bandeja} onAbrir={abrirHallazgo} onAnotar={anotarHallazgo}
              vacio={bandeja === `eliminados` ? `Nadie borró mensajes en tus chats (o todavía no llegó ninguno desde que el gestor los guarda).` : `No hay mensajes de «ver una vez».`} />
          )}
          {!esLista && orden.length === 0 && !(mensajesEncontrados?.length) && (
            <div className="gx-vacio">
              {buscar ? `Nada coincide con «${buscar}».`
                : bandeja === `responder` ? <><b>Al día.</b> Nadie espera tu respuesta.</>
                : cuentas.ocultos > 0 && hayModo ? <>El modo esconde lo de esta bandeja. <button type="button" className="gx-enlace" onClick={() => setBandeja(`ocultos`)}>Ver ocultos</button></>
                : `Nada por aquí.`}
            </div>
          )}
          {!esLista && secciones.map(([nombre, cs]) => (
            <div key={nombre}>
              <div className="gx-seccion-fecha">{nombre}</div>
              {cs.map((c) => (
                <Tarjeta key={c.id} chat={c} api={modosApi} elegida={c.id === elegidoId} repetido={repetidos.has(c.nombre)} conocidas={conocidas}
                  onElegir={() => setElegidoId(c.id)} onCambiar={(cambios) => cambiarChat(c.id, cambios)}
                  onOcultar={(o) => ocultarChat(c.id, o)}
                  onRenombrar={() => { setElegidoId(c.id); setInfo(true); setTimeout(() => setEditandoNombre(true), 0); }} />
              ))}
            </div>
          ))}
          {!esLista && mensajesEncontrados !== undefined && (
            <div>
              <div className="gx-seccion-fecha">Mensajes{mensajesEncontrados ? ` · ${mensajesEncontrados.length}${mensajesEncontrados.length === 100 ? `+` : ``}` : ``}</div>
              <ListaHallazgos lista={mensajesEncontrados} onAbrir={abrirHallazgo} vacio={`Ningún mensaje dice «${buscar.trim()}».`} />
            </div>
          )}
        </div>
      </section>

      {elegido ? (
        <ConversacionGestor key={elegido.id} chat={elegido} cliente={cliente} modosApi={modosApi} plantillas={plantillas}
          recordatorios={recordatorios.filter((r) => r.chatId === elegido.id)}
          onRecordatorios={async () => { await cargarRecordatorios(); await cargarChats(); }}
          onVolver={() => setElegidoId(null)} onFallo={alFallar} infoAbierta={info} onInfo={() => setInfo(!info)}
          onCambiarChat={(cambios) => cambiarChat(elegido.id, cambios)} />
      ) : (
        <div className="gx-conv">
          <div className="gx-elige">
            <b>Elige una conversación</b>
            <span><kbd>/</kbd> buscar · <kbd>Alt</kbd>+<kbd>↓</kbd> siguiente · <kbd>Esc</kbd> candado</span>
          </div>
        </div>
      )}

      {elegido && info && (
        <PanelContacto chat={elegido} api={modosApi} cliente={cliente} conocidas={conocidas} onCambiar={(cambios) => cambiarChat(elegido.id, cambios)} onCerrar={() => setInfo(false)}
          onOcultar={(o) => ocultarChat(elegido.id, o)}
          editandoNombre={editandoNombre} onEditarNombre={setEditandoNombre} />
      )}

      {configurando && (
        <ModosGestor api={modosApi} datos={modos} tema={tema} onCerrar={() => setConfigurando(false)}
          onCambio={async () => { await Promise.all([cargarModos(), cargarPlantillas()]); await cargarChats(); }} />
      )}

      {resumenAbierto && <ModalResumen api={modosApi} onCerrar={() => setResumenAbierto(false)} />}
      {pinModal && <ModalPinOcultos api={modosApi} configurado={pinModal.configurado} onAbierto={alAbrirOcultos} onCerrar={() => setPinModal(null)} />}

      {nuevoAbierto && (
        <NuevoChat api={modosApi} onCerrar={() => setNuevoAbierto(false)}
          onCreado={async (chatId) => { setNuevoAbierto(false); setBandeja(`todo`); setBuscar(``); await cargarChats(); setElegidoId(chatId); }} />
      )}
    </div>
  );
}
